import { action, computed, makeObservable, observable, override } from 'mobx';
import { FileBase } from './base';
import { FileEntityType } from 'src/api/api-types/files';
import { FilesApi } from 'src/api/files';
import { ApiReq, emptyValue } from 'src/api';
import { isFulfilled, isPending } from 'src/utils/common';
import { FileTokenService } from './services/file-token.service';
import { authStore } from '../../stores/auth';

export class GalleryFile extends FileBase {
  readonly api = new FilesApi();
  readonly original: string;
  readonly type: string;
  readonly thumbnail: string;  //must by necessary for a gallery

  @observable fileTokenService = new FileTokenService();
  @observable.ref fileReq: ApiReq<Blob> = emptyValue;
  @observable.ref previewFileReq: ApiReq<Blob> = emptyValue;
  @observable.ref previewFileSmallReq: ApiReq<Blob> = emptyValue;
  @observable.ref previewFileMediumReq: ApiReq<Blob> = emptyValue;

  constructor(
    file: FileEntityType,
    public attachmentId?: number,
    public fileId?: number,

  ) {
    super(file);
    this.original = `${file.id}`;
    this.thumbnail = `${file.id}`;
    this.type = this.extension;
    makeObservable(this);

  }

  @computed get isLoading() {
    return isPending(this.fileReq) || this.fileTokenService.isTokenLoading;
  }

  @computed get fileData() {
    if (!isFulfilled(this.fileReq) || !this.fileReq.value) return null;
    return (this.fileReq.value as { data: Blob }).data;
  }

  @computed get fileToken() {
    return this.fileTokenService.fileToken;
  }

  @computed get thumbnailSrc() {
    if (!this.fileToken) {
      return ''
    }

    if (this.isJpgPreview) {
      return this.api.getTextFilesUrlByToken(this.fileToken, 'jpg', 128);
    }
    if (this.isAvifPreview) {
      return this.api.getTextFilesUrlByToken(this.fileToken, 'avif', 128);
    }
    return '';
  }

  @computed get previewMediumSrcUrl() {
    if (!this.fileToken) {
      return ''
    }
    if (this.isPdf && this.isPdfPreview) {
      return this.api.getTextFilesUrlByToken(this.fileToken, 'pdf');
    }

    if ( this.isJpgPreview) {
      return this.api.getTextFilesUrlByToken(this.fileToken, 'jpg', 2048);
    }

    if (this.isAvifPreview) {
      return this.api.getTextFilesUrlByToken(this.fileToken, 'avif', 2048);
    }

    return '';
  }

  @computed get hasPreview() {
    return !!this.previewMediumSrcUrl && this.isImage;
  }

  @computed get isSvg() {
    return this.extension === 'svg' || this.extension === 'image/svg+xml';
  }

  @computed get isIcon() {
    return !this.isImage && !this.isPdf;
  }

  @action setFileToken = (token: string) => {
      this.fileTokenService.setFileToken(token);
  }

  @action initPreview = async () => {
    if (this.previewFormats.length > 0) {
      return this.processFileToken()
    }
  }

  @action processFileToken = async () => {
    if (!this.fileId || !this.attachmentId) return;
    if (this.fileToken) return;
    await this.fileTokenService.getFileDownloadToken(this.fileId, this.attachmentId);
  };

  @action errorHandling = async (error?: unknown, onFail?: (status?: number) => void) => {
    // eslint-disable-next-line no-console
    if (error) console.debug('Img error received:', error);
    if (!this.fileToken || !this.fileId || !this.attachmentId) return;
    // eslint-disable-next-line no-console
    console.log(error);
    try {
      const checkRequest = this.api.getFilePreview(this.fileToken, 'jpg');
      await checkRequest;
    } catch (reqError: any) {
      const status = reqError?.response?.status ?? reqError?.status;

      // eslint-disable-next-line no-console
      console.debug('Preview check failed with status:', status);

      if (status === 401 || status === 403) {
        try {
          await authStore.configuration.authorizeFileToken();
          await this.fileTokenService.getFileDownloadToken(this.fileId, this.attachmentId);

          return;
        } catch (retryErr) {
          // eslint-disable-next-line no-console
          console.error('Retry after authorize failed:', retryErr);
        }
      }

      onFail && onFail()
      throw reqError;
    }
  }

  @action fetchSrcOnlyForPreview = async (
    abortController?: AbortController,
  ) => {
    if (this.isPdf || this.isImage) {
      try {
        return await this.fetchSrc(abortController);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error in fetchSrcOnlyForPreview:', error);
        throw error;
      }
    }
  };



  @override async fetchSrc(abortController?: AbortController) {
    if (!!this.src || !this.fileId || !this.attachmentId) return;

    try {
      if (!this.fileToken) {
        await this.fileTokenService.getFileDownloadToken(this.fileId, this.attachmentId);

        if (!this.fileToken) {
          // eslint-disable-next-line no-console
          console.error('Failed to get file token');
          return;
        }
      }

      this.filesReq = this.api.getFilesUrlByToken(
        this.fileToken,
        abortController,
      );
      await this.filesReq;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error in fetchSrc:', error);
      if (error instanceof DOMException && error.name === 'AbortError') {
        // eslint-disable-next-line no-console
        console.error('Request was aborted');
      }
      throw error;
    }
  }
}
